// src/components/MySubscription.tsx
// "Mi suscripción": estado de cuenta, historial de pagos y formas de pago
// (MercadoPago + transferencia directa con alias y WhatsApp).
// IMPORTANTE: accesible también con suscripción vencida — el que debe,
// siempre tiene que poder ver su cuenta y pagar.

import React, { useEffect, useState } from 'react';
import {
  getSubscriptionStatus,
  createMercadoPagoPreference,
  SubscriptionStatus,
} from '../services/api';
import {
  CreditCard, Copy, Check, MessageCircle, Crown, Calendar,
  Receipt, Landmark, AlertCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface PaymentRow {
  id: number;
  amount: string | number;
  payment_method: string;
  status: string;
  payment_date: string;
}

interface BillingInfo {
  alias: string | null;
  holder: string | null;
  whatsapp: string;
}

const fmtARS = (n: number) => `$${n.toLocaleString('es-AR')}`;

const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' });
};

const METHOD_LABEL: Record<string, string> = {
  mercadopago: 'Mercado Pago',
  transferencia: 'Transferencia',
  efectivo: 'Efectivo',
};

const MySubscription: React.FC = () => {
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    const authHeaders = { Authorization: `Bearer ${token}` };

    const load = async () => {
      try {
        const [st, payRes, billRes] = await Promise.all([
          getSubscriptionStatus().catch(() => null),
          fetch(`${API_URL}/api/subscriptions/my-payments`, { headers: authHeaders })
            .then((r) => (r.ok ? r.json() : null)).catch(() => null),
          fetch(`${API_URL}/api/subscriptions/billing-info`, { headers: authHeaders })
            .then((r) => (r.ok ? r.json() : null)).catch(() => null),
        ]);
        if (cancelled) return;
        if (st) setStatus(st);
        if (payRes?.success) setPayments(payRes.payments || []);
        if (billRes?.success) setBilling(billRes.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const handlePayMP = async () => {
    try {
      setPaying(true);
      setError(null);
      const response = await createMercadoPagoPreference();
      if (response.init_point) {
        window.location.href = response.init_point;
      } else {
        throw new Error('No se pudo generar el link de pago');
      }
    } catch (err: any) {
      setError(err.message || 'Error al generar el pago');
      setPaying(false);
    }
  };

  const copyAlias = async () => {
    if (!billing?.alias) return;
    try {
      await navigator.clipboard.writeText(billing.alias);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = billing.alias;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const waText = encodeURIComponent(
    'Hola Esteban! Te envío el comprobante de mi suscripción a ConsultAR.'
  );
  const waUrl = billing ? `https://wa.me/${billing.whatsapp}?text=${waText}` : '#';

  const isActive = status?.status === 'trial' || status?.status === 'active';
  const price = typeof status?.monthly_price === 'number' ? status.monthly_price : null;

  const statusBadge = () => {
    if (!status) return null;
    const map: Record<string, { label: string; cls: string }> = {
      active: { label: 'Activa', cls: 'bg-green-100 text-green-700 border-green-200' },
      trial: { label: 'Período de prueba', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
      expired: { label: 'Vencida', cls: 'bg-red-100 text-red-700 border-red-200' },
      cancelled: { label: 'Cancelada', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
    };
    const cfg = map[status.status] || map.expired;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border ${cfg.cls}`}>
        {cfg.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-indigo-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="max-w-3xl mx-auto px-4 pt-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Mi suscripción</h1>
        <p className="text-sm text-gray-500 mb-6">Estado de cuenta, pagos y formas de pago</p>

        {/* ── Estado de cuenta ─────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900">Estado de cuenta</h2>
              {status?.is_founder && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold border border-amber-200">
                  <Crown className="w-3 h-3" /> Founder
                </span>
              )}
            </div>
            {statusBadge()}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
              <p className="text-xs uppercase tracking-wide font-semibold text-gray-400 mb-1">Plan mensual</p>
              <p className="text-2xl font-extrabold text-indigo-600">
                {price !== null ? `${fmtARS(price)}` : '—'}
                <span className="text-sm font-normal text-gray-500"> /mes</span>
              </p>
            </div>
            <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
              <p className="text-xs uppercase tracking-wide font-semibold text-gray-400 mb-1">
                <Calendar className="w-3 h-3 inline mr-1" />
                {status?.status === 'trial' ? 'Prueba hasta' : 'Próximo vencimiento'}
              </p>
              <p className="text-lg font-bold text-gray-800">
                {status?.status === 'trial'
                  ? fmtDate(status?.trial_ends_at)
                  : fmtDate((status as any)?.current_period_end)}
              </p>
            </div>
          </div>

          {!isActive && (
            <div className="mt-4 flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">
                Tu suscripción está vencida. Renovala con cualquiera de las formas de pago de abajo
                para recuperar el acceso — tus datos están intactos.
              </p>
            </div>
          )}
        </div>

        {/* ── Formas de pago ───────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Formas de pago</h2>

          {error && (
            <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Mercado Pago */}
            <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-4 flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="w-5 h-5 text-indigo-600" />
                <h3 className="font-semibold text-gray-900">Mercado Pago</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4 flex-1">
                Tarjeta, dinero en cuenta o cuotas. Se acredita al instante y tu cuenta se
                renueva automáticamente.
              </p>
              <button
                onClick={handlePayMP}
                disabled={paying}
                className="w-full py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-60"
              >
                {paying ? 'Generando link…' : 'Pagar con Mercado Pago'}
              </button>
            </div>

            {/* Transferencia directa */}
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4 flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <Landmark className="w-5 h-5 text-emerald-600" />
                <h3 className="font-semibold text-gray-900">Transferencia directa</h3>
              </div>

              {billing?.alias ? (
                <div className="mb-3">
                  <p className="text-xs uppercase tracking-wide font-semibold text-gray-400 mb-1">Alias</p>
                  <div className="flex items-center gap-2">
                    <code className="text-sm font-mono font-bold text-gray-800 bg-white border border-gray-200 rounded-lg px-2 py-1">
                      {billing.alias}
                    </code>
                    <button
                      onClick={copyAlias}
                      className="p-1.5 rounded-lg text-emerald-700 hover:bg-emerald-100 transition-colors"
                      title="Copiar alias"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  {billing.holder && (
                    <p className="text-xs text-gray-500 mt-1">Titular: {billing.holder}</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-600 mb-3">
                  Escribinos por WhatsApp y te pasamos el alias para transferir.
                </p>
              )}

              <p className="text-xs text-gray-500 mb-3 flex-1">
                Después de transferir, envianos el comprobante — tu cuenta se acredita al
                recibirlo.
              </p>
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                Enviar comprobante
              </a>
            </div>
          </div>
        </div>

        {/* ── Historial de pagos ───────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Receipt className="w-5 h-5 text-gray-400" />
            <h2 className="text-lg font-bold text-gray-900">Historial de pagos</h2>
          </div>

          {payments.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">
              Todavía no hay pagos registrados.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                    <th className="py-2 pr-3 font-semibold">Fecha</th>
                    <th className="py-2 pr-3 font-semibold">Monto</th>
                    <th className="py-2 pr-3 font-semibold">Método</th>
                    <th className="py-2 font-semibold">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-2.5 pr-3 text-gray-700">{fmtDate(p.payment_date)}</td>
                      <td className="py-2.5 pr-3 font-semibold text-gray-900">
                        {fmtARS(parseFloat(String(p.amount)))}
                      </td>
                      <td className="py-2.5 pr-3 text-gray-600">
                        {METHOD_LABEL[p.payment_method] || p.payment_method || '—'}
                      </td>
                      <td className="py-2.5">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          p.status === 'approved'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {p.status === 'approved' ? 'Acreditado' : p.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MySubscription;
